import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type CustomerSummary } from '../lib/api.js';
import { useAuth } from '../lib/auth-store.js';
import { useCart } from '../lib/cart-store.js';
import { useFavorites } from '../lib/favorites-store.js';
import { useOutbox } from '../lib/outbox-store.js';
import { useRecent } from '../lib/recent-store.js';
import { toast } from '../lib/toast-store.js';
import { initials, money0, money2, shortDate } from '../lib/format.js';
import { IconCart, IconSearch } from '../components/icons.js';

/**
 * Rep home / dashboard. The first screen after login: a quick read on the rep's
 * book of business (scoped to their salesperson code by the API), the accounts
 * that need attention today, work they can resume (cart / queued orders), and
 * fast jumps into the sales surfaces.
 *
 * Everything here is derived from data the app already has — the scoped customer
 * list plus local cart/outbox/recent state — so it adds no new API surface.
 */
export function HomeScreen() {
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const cart = useCart((s) => s.items);
  const addToCart = useCart((s) => s.add);
  const outbox = useOutbox((s) => s.orders);
  const recents = useRecent((s) => s.clients);
  const favorites = useFavorites((s) => s.items);

  // The rep's accounts, recent-first (API sorts by -lastInvoiceDate and scopes
  // to the caller's salesperson code). Pull a generous page for the rollups.
  const clients = useQuery({
    queryKey: ['customers', 'dashboard'],
    queryFn: ({ signal }) => api.customers.search({ limit: 500 }, signal),
    staleTime: 60_000,
  });

  // Goal progress: the user's book YTD vs their admin-set annual target. For
  // reps the scoped `clients` page IS their whole book; a manager/admin with
  // their own code fetches that code's book explicitly (server allows it).
  const target = user?.salesTargetAnnual ?? 0;
  const goalCode = user?.spireSalespersonCode ?? null;
  const goalIsRep = user?.role === 'rep';
  const goalBook = useQuery({
    queryKey: ['customers', 'goal-book', goalCode],
    queryFn: ({ signal }) =>
      api.customers.search({ salesperson: goalCode!, limit: 500 }, signal),
    staleTime: 5 * 60_000,
    enabled: target > 0 && !!goalCode && !goalIsRep,
  });

  // Exact whole-book counts (a rep's 200 accounts or an admin's 39K) — the
  // 500-row page above is only for the YTD/top-account rollups.
  const stats = useQuery({
    queryKey: ['customers', 'stats'],
    queryFn: ({ signal }) => api.customers.stats(signal),
    staleTime: 5 * 60_000,
  });

  const prefetch = (spireId: number) =>
    void qc.prefetchQuery({ queryKey: ['customer', spireId], queryFn: () => api.customers.getById(spireId) });

  if (!user) return null;

  const list = clients.data ?? [];
  const capped = list.length >= 500;
  const holdAccounts = list.filter((c) => c.hold);
  const noOrders = list.filter((c) => Number(c.thisYearSales) <= 0).length;
  const ytdTotal = list.reduce((s, c) => s + Number(c.thisYearSales || 0), 0);
  const topAccounts = [...list]
    .filter((c) => Number(c.thisYearSales) > 0)
    .sort((a, b) => Number(b.thisYearSales) - Number(a.thisYearSales))
    .slice(0, 5);

  const cartTotal = cart.reduce((s, i) => s + i.qty * Number(i.unitPrice || 0), 0);
  const cartUnits = cart.reduce((s, i) => s + i.qty, 0);

  return (
    <>
      <div className="dash-hero">
        <div className="hi">{greeting()},</div>
        <div className="who">{user.displayName.split(' ')[0]}</div>
        {target > 0 && (goalIsRep || goalCode) && (() => {
          const achieved = goalIsRep
            ? ytdTotal
            : (goalBook.data ?? []).reduce((sum, c) => sum + Number(c.thisYearSales || 0), 0);
          const loading = goalIsRep ? clients.isPending : goalBook.isPending;
          const pct = Math.min(100, (achieved / target) * 100);
          const hit = achieved >= target;
          return (
            <div className="goal" aria-label={`Sales goal progress: ${Math.round(pct)} percent`}>
              <div className="row">
                <span>{loading ? 'Loading…' : `${money0(achieved)} of ${money0(target)} YTD`}</span>
                <span className="pct">{loading ? '' : hit ? '🎉 goal met' : `${Math.round(pct)}%`}</span>
              </div>
              <div className="track">
                <div className={`fill${hit ? ' hit' : ''}`} style={{ width: `${loading ? 0 : pct}%` }} />
              </div>
            </div>
          );
        })()}
        <div className="tags">
          <span className="tag" style={{ textTransform: 'capitalize' }}>{user.role}</span>
          {user.spireSalespersonCode && <span className="tag">Rep {user.spireSalespersonCode}</span>}
        </div>
      </div>

      <Link
        to="/search"
        style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 14px 0', padding: '11px 14px', background: '#fff', border: '1px solid var(--kd-line)', borderRadius: 12, color: 'var(--kd-text-3)', textDecoration: 'none', fontSize: 14 }}
      >
        <IconSearch size={16} /> Search clients or items
      </Link>

      {/* Book-of-business rollup. Counts are EXACT (Spire envelope counts,
          scope-aware); the YTD sum comes from the 500 most recently active
          accounts and is labeled honestly when that's a subset. */}
      <div className="stat-grid">
        <Stat
          value={stats.data ? stats.data.totalCount.toLocaleString() : clients.isPending ? '…' : `${list.length}${capped ? '+' : ''}`}
          label="My clients"
          to="/clients"
        />
        <Stat value={clients.isPending ? '…' : money0(ytdTotal)} label={capped ? 'Active book YTD' : 'Book YTD'} />
        <Stat
          value={stats.data ? String(stats.data.holdCount) : String(holdAccounts.length)}
          label="On A/R hold"
          danger={(stats.data?.holdCount ?? holdAccounts.length) > 0}
          {...((stats.data?.holdCount ?? holdAccounts.length) > 0
            ? { to: '/clients' as const, search: { filter: 'hold' as const } }
            : {})}
        />
        {capped ? (
          <Stat value="500" label="Recently active" />
        ) : (
          <Stat value={String(noOrders)} label="No orders YTD" />
        )}
      </div>
      {capped && <div className="dash-note">YTD total covers the 500 most recently active accounts.</div>}
      {clients.isError && (
        <div className="kd-error" style={{ margin: '0 14px 8px' }}>
          Couldn’t load your accounts.{' '}
          <button type="button" className="kd-retry" onClick={() => void clients.refetch()}>
            Retry
          </button>
        </div>
      )}

      {/* Resume in-progress work */}
      {(cart.length > 0 || outbox.length > 0) && <div className="section-title">Pick up where you left off</div>}
      {cart.length > 0 && (
        <Link to="/cart" className="resume-card">
          <span className="ic"><IconCart size={20} /></span>
          <div className="body">
            <div className="t">Cart in progress</div>
            <div className="s">{cartUnits} {cartUnits === 1 ? 'unit' : 'units'} · {money2(cartTotal)}</div>
          </div>
          <span className="go">›</span>
        </Link>
      )}
      {outbox.length > 0 && (
        <Link to="/cart" className="resume-card">
          <span className="ic warn">⏳</span>
          <div className="body">
            <div className="t">{outbox.length} {outbox.length === 1 ? 'order' : 'orders'} waiting to send</div>
            <div className="s">
              {outbox.some((o) => o.lastError) ? 'Tap to review — last attempt failed' : 'Will send when you’re back online'}
            </div>
          </div>
          <span className="go">›</span>
        </Link>
      )}

      {/* Accounts that need a look */}
      {holdAccounts.length > 0 && (
        <>
          <div className="section-title">Needs attention · A/R hold</div>
          {holdAccounts.slice(0, 5).map((c) => (
            <ClientRow key={c.spireId} c={c} onPrefetch={prefetch} rightLabel="A/R hold" danger />
          ))}
        </>
      )}

      {/* Top accounts by YTD */}
      {topAccounts.length > 0 && (
        <>
          <div className="section-title">Your top accounts</div>
          {topAccounts.map((c) => (
            <ClientRow
              key={c.spireId}
              c={c}
              onPrefetch={prefetch}
              rightLabel={`${money0(c.thisYearSales)} YTD`}
            />
          ))}
        </>
      )}

      {/* Recently visited */}
      {recents.length > 0 && (
        <>
          <div className="section-title">Recently visited</div>
          {recents.slice(0, 5).map((rc) => (
            <Link
              key={rc.spireId}
              to="/clients/$id"
              params={{ id: String(rc.spireId) }}
              className="customer-row"
              onPointerEnter={() => prefetch(rc.spireId)}
            >
              <div className="avatar">{initials(rc.name)}</div>
              <div className="body">
                <div className="name">{rc.name}</div>
              </div>
              <div className="right">›</div>
            </Link>
          ))}
        </>
      )}

      {/* Empty-state nudge when a freshly-onboarded rep has no scoped accounts */}
      {clients.isSuccess && list.length === 0 && (
        <div className="kd-muted" style={{ padding: '4px 18px 12px' }}>
          No accounts are assigned to your salesperson code yet. Browse all clients or search inventory below.
        </div>
      )}

      {/* Favorites / quick-order list */}
      {favorites.length > 0 && (
        <>
          <div className="section-title">Favorites · {favorites.length}</div>
          {favorites.slice(0, 8).map((f) => (
            <div className="resume-card" key={f.spireId}>
              <Link
                to="/inventory/$id"
                params={{ id: String(f.spireId) }}
                style={{ display: 'flex', gap: 12, flex: 1, minWidth: 0, alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
              >
                <span className="ic" style={{ background: '#fff6da', color: 'var(--kd-amber)' }}>★</span>
                <div className="body">
                  <div className="t">{f.partNo}</div>
                  <div className="s">
                    {[f.description, money2(f.price) + (f.uom ? ` / ${f.uom}` : '')].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </Link>
              <button
                type="button"
                className="kd-retry"
                style={{ background: 'var(--kd-green)' }}
                onClick={() => {
                  addToCart({
                    partNo: f.partNo,
                    whseCode: f.whseCode,
                    description: f.description,
                    unitPrice: f.price,
                    ...(f.uom ? { sellMeasure: f.uom } : {}),
                    qty: 1,
                  });
                  toast(`Added ${f.partNo}`);
                }}
              >
                + Add
              </button>
            </div>
          ))}
          {favorites.length > 8 && <div className="dash-note">+{favorites.length - 8} more favorites</div>}
        </>
      )}

      {/* Quick actions */}
      <div className="section-title">Quick actions</div>
      <div className="quick-actions">
        <button type="button" className="action-btn" onClick={() => void navigate({ to: '/clients' })}>
          Browse clients
        </button>
        <button type="button" className="action-btn" onClick={() => void navigate({ to: '/inventory' })}>
          <IconSearch size={16} /> Search inventory
        </button>
        <button type="button" className="action-btn" onClick={() => void navigate({ to: '/promotions' })}>
          🏷 Promotions
        </button>
      </div>
    </>
  );
}

function Stat({
  value,
  label,
  danger,
  to,
  search,
}: {
  value: string;
  label: string;
  danger?: boolean;
  to?: '/clients';
  search?: { filter: 'active' | 'hold' };
}) {
  const inner = (
    <>
      <div className={`v${danger ? ' danger' : ''}`}>{value}</div>
      <div className="l">{label}</div>
    </>
  );
  if (to) {
    return (
      <Link to={to} search={search ?? {}} className="stat stat-link">
        {inner}
      </Link>
    );
  }
  return <div className="stat">{inner}</div>;
}

function ClientRow({
  c,
  onPrefetch,
  rightLabel,
  danger,
}: {
  c: CustomerSummary;
  onPrefetch: (id: number) => void;
  rightLabel: string;
  danger?: boolean;
}) {
  return (
    <Link
      to="/clients/$id"
      params={{ id: String(c.spireId) }}
      className="customer-row"
      onPointerEnter={() => onPrefetch(c.spireId)}
      onFocus={() => onPrefetch(c.spireId)}
    >
      <div className="avatar">{initials(c.name)}</div>
      <div className="body">
        <div className="name">{c.name}</div>
        <div className="meta">
          {[c.address.city, c.address.provState].filter(Boolean).join(', ') || c.customerNo}
          {c.lastInvoiceDate ? ` · last ${shortDate(c.lastInvoiceDate)}` : ''}
        </div>
      </div>
      <div className="right">
        <div className="strong" style={danger ? { color: 'var(--kd-danger)' } : undefined}>{rightLabel}</div>
      </div>
    </Link>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}
